/** Read-only, copyable list of the inbound webhook endpoint URLs for a booking
 *  integration account.
 *
 *  Shape (mirrors the backend routes in `apiv2/urls_integrations.py`):
 *    single account:  {api}/v2/{topcode}/integration/{restech}/{type}/
 *    named account:   {api}/v2/{topcode}/integration/{restech}/{slug}/{type}/
 *  where {type} ∈ booking | order | departure | product.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { config } from '@/config';
import { useSnackbar } from '@/components/Snackbar';

export const WEBHOOK_TYPES = ['booking', 'order', 'departure', 'product'] as const;

/** The public `/v2` API base — the same origin as the console API but without
 *  the `/console` prefix. Derived from the configured console base so the URL is
 *  correct per environment (dev/staging/prod). */
function apiV2Base(): string {
  return config.apiBaseUrl.replace(/\/console\/?$/, '').replace(/\/+$/, '');
}

export function webhookUrl(
  toCode: string,
  restech: string,
  slug: string | null | undefined,
  type: string,
): string {
  const seg = slug ? `/${slug}` : '';
  return `${apiV2Base()}/${toCode}/integration/${restech}${seg}/${type}/`;
}

interface Props {
  toCode: string;
  restech: string;
  slug: string | null | undefined;
}

export function WebhookUrls({ toCode, restech, slug }: Props) {
  const snackbar = useSnackbar();

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      snackbar.show('Webhook URL copied.', 'success');
    } catch {
      snackbar.show('Could not copy to clipboard.', 'error');
    }
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Webhook endpoints
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Configure these URLs in {restech} to deliver events to us.
      </Typography>
      {WEBHOOK_TYPES.map((t) => {
        const url = webhookUrl(toCode, restech, slug, t);
        return (
          <TextField
            key={t}
            label={t.charAt(0).toUpperCase() + t.slice(1)}
            value={url}
            size="small"
            fullWidth
            margin="dense"
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: 12 },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy URL">
                    <IconButton size="small" edge="end" onClick={() => void copy(url)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        );
      })}
    </Box>
  );
}
